/**
 * Modal de Registro de Feedback
 * Permite registrar o resultado de uma ligação para cliente
 */
import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { registrarFeedbackCliente } from '../../services/api';
import './RegistrarFeedbackModal.css';

const RESULTADOS = [
    { key: 'positivo', label: 'Respondeu Positivamente', emoji: '✅', cor: '#10b981', descricao: 'Cliente interessado, vai visitar a loja' },
    { key: 'comprou', label: 'Comprou', emoji: '🛒', cor: '#22c55e', descricao: 'Cliente fez uma compra' },
    { key: 'agendado', label: 'Agendou Retorno', emoji: '📅', cor: '#3b82f6', descricao: 'Cliente pediu para ligar em outra hora/dia' },
    { key: 'ligar_depois', label: 'Ligar Depois', emoji: '⏳', cor: '#8b5cf6', descricao: 'Não foi possível falar, tentar novamente' },
    { key: 'nao_atendeu', label: 'Não Atendeu', emoji: '📵', cor: '#f59e0b', descricao: 'Chamou mas não atendeu' },
    { key: 'nao_perturbar', label: 'Não Quer Ser Perturbado', emoji: '🚫', cor: '#ef4444', descricao: 'Cliente pediu para não ligar mais' },
    { key: 'numero_invalido', label: 'Número Inválido', emoji: '❌', cor: '#6b7280', descricao: 'Número errado, inexistente ou caixa postal' },
    { key: 'outro', label: 'Outro', emoji: '📝', cor: '#9ca3af', descricao: 'Outro resultado não listado' }
];

const RegistrarFeedbackModal = ({
    show,
    onHide,
    cliente,
    onSuccess
}) => {
    const [resultadoSelecionado, setResultadoSelecionado] = useState('');
    const [observacao, setObservacao] = useState('');
    const [comprou, setComprou] = useState(false);
    const [valorCompra, setValorCompra] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sucesso, setSucesso] = useState(false);

    const handleResultadoChange = (key) => {
        setResultadoSelecionado(key);
        // Se escolheu "comprou", ativar campo de valor
        if (key === 'comprou') {
            setComprou(true);
        } else {
            setComprou(false);
            setValorCompra('');
        }
    };

    const handleSubmit = async () => {
        if (!resultadoSelecionado) {
            setError('Selecione um resultado');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await registrarFeedbackCliente(
                cliente.id,
                resultadoSelecionado,
                observacao,
                comprou,
                comprou ? parseFloat(valorCompra.replace(',', '.')) || 0 : 0
            );

            if (response.success) {
                setSucesso(true);
                setTimeout(() => {
                    onSuccess && onSuccess(cliente, resultadoSelecionado);
                    handleClose();
                }, 1000);
            } else {
                setError(response.message || 'Erro ao registrar feedback');
            }
        } catch (err) {
            setError('Erro de conexão');
            console.error('[RegistrarFeedback] Erro:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setResultadoSelecionado('');
        setObservacao('');
        setComprou(false);
        setValorCompra('');
        setError(null);
        setSucesso(false);
        onHide();
    };

    const formatarTelefone = (telefone) => {
        if (!telefone) return '-';
        return telefone.replace(/\D/g, '');
    };

    if (!cliente) return null;

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" className="feedback-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold">
                    📞 Registrar Resultado da Ligação
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Info do Cliente */}
                <div className="cliente-info-header mb-4">
                    <div className="cliente-avatar">
                        {cliente.cliente_nome?.charAt(0) || '?'}
                    </div>
                    <div className="cliente-details">
                        <h5 className="mb-1">{cliente.cliente_nome || 'Cliente'}</h5>
                        <div className="cliente-meta">
                            {cliente.cliente_telefone && (
                                <a
                                    href={`https://wa.me/55${formatarTelefone(cliente.cliente_telefone)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cliente-phone"
                                >
                                    📱 {cliente.cliente_telefone}
                                </a>
                            )}
                            {cliente.cliente_cidade && (
                                <span className="cliente-city">📍 {cliente.cliente_cidade}</span>
                            )}
                            <Badge bg="warning" text="dark">
                                ⏱️ {cliente.dias_sem_comprar} dias sem comprar
                            </Badge>
                        </div>
                    </div>
                </div>

                {sucesso ? (
                    <Alert variant="success" className="text-center">
                        <div style={{ fontSize: '3rem' }}>✅</div>
                        <h5 className="mt-2">Feedback registrado com sucesso!</h5>
                    </Alert>
                ) : (
                    <>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Seleção de Resultado */}
                        <Form.Label className="fw-semibold mb-3">Qual foi o resultado da ligação?</Form.Label>

                        <div className="resultado-grid">
                            {RESULTADOS.map(resultado => (
                                <div
                                    key={resultado.key}
                                    className={`resultado-card ${resultadoSelecionado === resultado.key ? 'selected' : ''}`}
                                    onClick={() => handleResultadoChange(resultado.key)}
                                    style={{
                                        '--resultado-color': resultado.cor,
                                        borderColor: resultadoSelecionado === resultado.key ? resultado.cor : undefined
                                    }}
                                >
                                    <div className="resultado-emoji">{resultado.emoji}</div>
                                    <div className="resultado-label">{resultado.label}</div>
                                    <div className="resultado-descricao">{resultado.descricao}</div>
                                </div>
                            ))}
                        </div>

                        {/* Se comprou, mostrar campo de valor */}
                        {comprou && (
                            <Row className="mt-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">💰 Valor da Compra</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="R$ 0,00"
                                            value={valorCompra}
                                            onChange={(e) => setValorCompra(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {/* Observação */}
                        <Form.Group className="mt-4">
                            <Form.Label className="fw-semibold">📝 Observação (opcional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Adicione detalhes sobre a conversa..."
                                value={observacao}
                                onChange={(e) => setObservacao(e.target.value)}
                            />
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                {!sucesso && (
                    <>
                        <Button variant="outline-secondary" onClick={handleClose}>
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={loading || !resultadoSelecionado}
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar Feedback'
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default RegistrarFeedbackModal;
